/**
 * 피처 플래그 React Hook
 *
 * useAuth와 통합하여 현재 사용자의 피처 플래그 접근 권한을 확인합니다.
 */
import { FeatureFlagName } from "constants/featureFlags";
import { FeatureFlagsContext } from "contexts/FeatureFlagsContext";
import { useAuth } from "hooks/useAuth";
import { useContext, useMemo } from "react";
import {
    FeatureFlagUser,
    canViewFeature,
    canViewFeatureFromData
} from "utils/featureFlagsEvaluator";

/**
 * 피처 플래그 접근을 위한 React Hook
 *
 * @returns 피처 플래그 확인 함수와 유틸리티
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isFeatureEnabled } = useFeatureFlags();
 *
 *   if (isFeatureEnabled("CHRISTMAS_EVENT")) {
 *     return <ChristmasEvent />;
 *   }
 *
 *   return <DefaultTheme />;
 * }
 * ```
 */
export function useFeatureFlags() {
    const { me, isLoggedIn } = useAuth();
    const { flags, loading, error, isFromHasura, refetch } =
        useContext(FeatureFlagsContext);

    /**
     * 현재 사용자 정보를 피처 플래그용으로 변환
     */
    const featureFlagUser: FeatureFlagUser | null = useMemo(() => {
        if (!isLoggedIn || !me) {
            return null; // guest로 처리됨
        }

        return {
            id: me.id,
            // JWT에서 가져온 역할 사용
            // 로그인한 사용자는 "user" 역할을 가짐
            role: "user" as const
        };
    }, [me, isLoggedIn]);

    /**
     * 특정 피처가 현재 사용자에게 활성화되어 있는지 확인
     *
     * Hasura에서 플래그를 가져온 경우 해당 데이터를 사용하고,
     * 그렇지 않으면 로컬 상수를 사용합니다.
     *
     * @param featureName 피처 플래그 이름
     * @returns 피처가 활성화되었는지 여부
     */
    const isFeatureEnabled = useMemo(
        () => (featureName: FeatureFlagName) => {
            const flagData = flags.get(featureName);

            if (flagData) {
                // Hasura 또는 로컬 플래그 데이터 사용
                return canViewFeatureFromData(flagData, featureFlagUser);
            }

            // 플래그를 찾을 수 없으면 로컬 상수로 폴백
            return canViewFeature(featureName, featureFlagUser);
        },
        [flags, featureFlagUser]
    );

    return {
        /** 피처 활성화 여부 확인 함수 */
        isFeatureEnabled,
        /** 현재 사용자 정보 (디버깅용) */
        user: featureFlagUser,
        /** 플래그 로딩 중 */
        loading,
        /** 에러 */
        error,
        /** Hasura에서 가져왔는지 여부 */
        isFromHasura,
        /** 플래그 새로고침 */
        refetch
    };
}
